import { FC, MouseEvent, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { Sizes } from 'enums/sizes';
import { Button } from 'components/ui/Button';
import {
  CheckIcon,
  DocumentIcon,
  TrashIcon,
  UploadIcon
} from 'components/icons/Icons';
import { AlertCircle, Eye, RefreshCw } from 'lucide-react';
import { DocumentVerificationStatusEnum } from 'api/actor/models/DocumentVerificationStatusEnum';
import { useDialog } from 'hooks/useDialog';
import { useDocumentViewerDialog } from 'components/domain/Document/useDocumentViewerDialog';
import IdentityVerificationDialog from './IdentityVerificationDialog';

type UploaderMode = 'empty' | 'pending' | 'verified' | 'rejected';

type ButtonMouseEvent = MouseEvent<HTMLButtonElement | HTMLAnchorElement>;

interface IdentityDocumentUploaderProps {
  documentName: string;
  naturalPersonId: string;
  personDisplayName?: string;
  storedFileId?: string;
  verificationStatus?: DocumentVerificationStatusEnum;
  onSuccess: (storedFileId: string) => void;
  onRemove?: (storedFileId: string) => void;
  disabled?: boolean;
}

const IdentityDocumentUploader: FC<IdentityDocumentUploaderProps> = ({
  documentName,
  naturalPersonId,
  personDisplayName,
  storedFileId,
  verificationStatus,
  onSuccess,
  onRemove,
  disabled = false
}) => {
  const { t } = useTranslation(NsKey.IdentityUpload);
  const { openBlankDialog, closeDialog } = useDialog();
  const { openDocumentViewer } = useDocumentViewerDialog();

  const openDialog = useCallback(
    (options?: { initialStoredFileId?: string }) => {
      openBlankDialog(
        { size: Sizes['2xl'] },
        <IdentityVerificationDialog
          naturalPersonId={naturalPersonId}
          personDisplayName={personDisplayName}
          initialStoredFileId={options?.initialStoredFileId}
          onSuccess={(newStoredFileId) => {
            onSuccess(newStoredFileId);
            closeDialog();
          }}
          onClose={closeDialog}
        />
      );
    },
    [openBlankDialog, closeDialog, naturalPersonId, personDisplayName, onSuccess]
  );

  const handleView = useCallback(() => {
    if (!storedFileId) return;
    openDocumentViewer({
      title: documentName,
      storedFileId,
      showDefaultActions: true
    });
  }, [storedFileId, documentName, openDocumentViewer]);

  const handleRemove = useCallback(() => {
    if (storedFileId) onRemove?.(storedFileId);
  }, [storedFileId, onRemove]);

  const openManualVerification = useCallback(() => {
    if (!storedFileId) return;
    openDialog({ initialStoredFileId: storedFileId });
  }, [storedFileId, openDialog]);

  const stopAnd = useCallback(
    (handler: () => void) => (e: ButtonMouseEvent) => {
      e.stopPropagation();
      handler();
    },
    []
  );

  const mode: UploaderMode = !storedFileId
    ? 'empty'
    : verificationStatus === DocumentVerificationStatusEnum.Verified
      ? 'verified'
      : verificationStatus === DocumentVerificationStatusEnum.Rejected
        ? 'rejected'
        : 'pending';

  const viewButton = (
    <Button type="button" plain onClick={stopAnd(handleView)} title="Visualiser">
      <Eye className="w-4 h-4 text-gray-500 hover:text-primary" />
    </Button>
  );

  const replaceButton = (
    <Button
      type="button"
      plain
      onClick={stopAnd(openDialog)}
      disabled={disabled}
      title={t('IDENTITY_UPLOAD.FILE_SECTION.REPLACE')}
    >
      <UploadIcon className="w-4 h-4 text-gray-500 hover:text-primary" />
    </Button>
  );

  const removeButton = onRemove ? (
    <Button
      type="button"
      plain
      onClick={stopAnd(handleRemove)}
      disabled={disabled}
      title="Supprimer"
    >
      <TrashIcon className="w-4 h-4 text-gray-500 hover:text-red-500" />
    </Button>
  ) : null;

  if (mode === 'empty') {
    return (
      <UploaderCard
        tone="default"
        icon={<DocumentIcon className="w-5 h-5 text-gray-500" />}
        title={<span className="font-medium text-sm">{documentName}</span>}
        description={t('IDENTITY_UPLOAD.FILE_SECTION.INSTRUCTIONS')}
        actions={
          <Button
            color="primary"
            onClick={stopAnd(() => openDialog())}
            disabled={disabled}
            title={t('IDENTITY_UPLOAD.TITLE')}
          >
            <UploadIcon className="w-4 h-4" />
          </Button>
        }
        onClick={disabled ? undefined : () => openDialog()}
      />
    );
  }

  const title = (
    <span
      className="font-medium text-sm hover:text-primary hover:underline cursor-pointer transition-colors"
      onClick={stopAnd(handleView)}
    >
      {documentName}
    </span>
  );

  if (mode === 'rejected') {
    return (
      <UploaderCard
        tone="danger"
        icon={<AlertCircle className="w-5 h-5 text-red-600" />}
        title={title}
        description={
          <div className="flex flex-col">
            <span className="text-xs font-semibold text-red-700 mt-0.5">
              {t('IDENTITY_UPLOAD.STATUS.REJECTED_TITLE')}
            </span>
            <span className="text-xs text-red-600">
              {t('IDENTITY_UPLOAD.STATUS.REJECTED_MESSAGE')}
            </span>
          </div>
        }
        actions={
          <>
            <Button color="red" onClick={stopAnd(() => openDialog())} disabled={disabled}>
              <UploadIcon className="w-4 h-4 mr-1" />
              {t('IDENTITY_UPLOAD.STATUS.REJECTED_REUPLOAD')}
            </Button>
            {viewButton}
            {removeButton}
          </>
        }
      />
    );
  }

  if (mode === 'verified') {
    return (
      <UploaderCard
        tone="default"
        icon={<DocumentIcon className="w-5 h-5 text-gray-500" />}
        title={title}
        actions={
          <>
            <div className="flex items-center gap-1.5 text-green-600">
              <CheckIcon className="w-4 h-4" />
              <span className="text-xs">{t('IDENTITY_UPLOAD.STATUS.VERIFIED')}</span>
            </div>
            <div className="flex items-center gap-1">
              {viewButton}
              {replaceButton}
              {removeButton}
            </div>
          </>
        }
      />
    );
  }

  return (
    <UploaderCard
      tone="warning"
      icon={<DocumentIcon className="w-5 h-5 text-amber-600" />}
      title={title}
      description={
        <span className="text-xs text-amber-700">
          {t('IDENTITY_UPLOAD.STATUS.PENDING')}
        </span>
      }
      actions={
        <>
          <Button
            color="primary"
            onClick={stopAnd(openManualVerification)}
            disabled={disabled}
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            {t('IDENTITY_UPLOAD.STATUS.PENDING_RETRY')}
          </Button>
          {viewButton}
          {removeButton}
        </>
      }
    />
  );
};

type CardTone = 'default' | 'warning' | 'danger';

const TONE_CLASSES: Record<CardTone, string> = {
  default: 'border-gray-200 bg-white',
  warning: 'border-amber-200 bg-amber-50',
  danger: 'border-red-200 bg-red-50'
};

interface UploaderCardProps {
  tone: CardTone;
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions: ReactNode;
  onClick?: () => void;
}

const UploaderCard: FC<UploaderCardProps> = ({
  tone,
  icon,
  title,
  description,
  actions,
  onClick
}) => {
  const baseClasses = `border rounded-lg p-4 transition-all duration-200 ${TONE_CLASSES[tone]}`;
  const interactiveClasses = onClick ? 'cursor-pointer hover:border-primary/60' : '';

  return (
    <div className={`${baseClasses} ${interactiveClasses}`} onClick={onClick}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0">{icon}</div>
          <div className="min-w-0">
            {title}
            {description && (
              <div className="mt-0.5 text-xs text-gray-500">{description}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">{actions}</div>
      </div>
    </div>
  );
};

export default IdentityDocumentUploader;
